import { create } from "zustand";
import type { SoundtrackConfig } from "@mind-sands/shared";

export interface SoundtrackStore {
  soundtracks: SoundtrackConfig[];
  enabledTrackIndices: number[];
  isPlaying: boolean;
  volume: number;
  isMuted: boolean;

  setSoundtracks: (tracks: SoundtrackConfig[]) => void;
  toggleSoundtrackPlay: () => void;
  setSoundtrackVolume: (volume: number) => void;
  toggleSoundtrackMute: () => void;
  toggleTrackEnabled: (index: number) => void;
  setEnabledTracks: (indices: number[]) => void;
  resetForSpaceLoad: () => void;
}

export const useSoundtrackStore = create<SoundtrackStore>((set) => ({
  soundtracks: [],
  enabledTrackIndices: [],
  isPlaying: false,
  volume: 0.5,
  isMuted: false,

  setSoundtracks: (tracks) =>
    set((state) => {
      const currentIds = state.soundtracks.map((t) => t.trackId);
      const nextIds = tracks.map((t) => t.trackId);
      const sameIds =
        currentIds.length === nextIds.length &&
        currentIds.every((id, i) => id === nextIds[i]);

      if (sameIds) {
        return { soundtracks: tracks };
      }

      const allIndices = tracks.map((_, i) => i);
      return {
        soundtracks: tracks,
        enabledTrackIndices: allIndices,
        isPlaying: tracks.length > 0,
      };
    }),

  toggleSoundtrackPlay: () => set((state) => ({ isPlaying: !state.isPlaying })),

  setSoundtrackVolume: (volume) =>
    set({ volume: Math.max(0, Math.min(1, volume)) }),

  toggleSoundtrackMute: () => set((state) => ({ isMuted: !state.isMuted })),

  toggleTrackEnabled: (index) =>
    set((state) => {
      const isEnabled = state.enabledTrackIndices.includes(index);
      return {
        enabledTrackIndices: isEnabled
          ? state.enabledTrackIndices.filter((i) => i !== index)
          : [...state.enabledTrackIndices, index],
      };
    }),

  setEnabledTracks: (indices) => set({ enabledTrackIndices: indices }),

  resetForSpaceLoad: () =>
    set({
      soundtracks: [],
      enabledTrackIndices: [],
      isPlaying: false,
    }),
}));
